import { useContext, useMemo } from 'react';
import { Button, Card, Col, Row, Select, Typography } from 'antd';
import { ClockCircleOutlined, DollarOutlined, FileTextOutlined, FilePdfOutlined, TeamOutlined } from '@ant-design/icons';
import useDashboard from './useDashboard';
import StatCard from './components/StatCard';
import InvoicesByTypeChart from './components/InvoicesByTypeChart';
import RegistrationHistoryChart from './components/RegistrationHistoryChart';
import InvoicesImportHistoryChart from './components/InvoicesImportHistoryChart';
import RaffleCalendar from './components/RaffleCalendar';
import TopCompaniesList from './components/TopCompaniesList';
import TopUsersList from './components/TopUsersList';
import SocialInstitutionsStats from './components/SocialInstitutionsStats';
import FinishedRafflesList from './components/FinishedRafflesList';
import ReportDashboard from './components/ReportDashboard';
import { ThemeContext } from '../../contexts/ThemeContext';
import { ConfigProvider, theme as antdTheme } from 'antd';
import useAuth from '../../hooks/useAuth';
import { ICity } from '../../interfaces';

const formatNumber = (value: number) =>
    new Intl.NumberFormat('pt-BR').format(Math.round(value));

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const currentYear = new Date().getFullYear();
const yearOptions = [
    { label: 'Todos', value: 'all' as const },
    ...Array.from({ length: 6 }, (_, i) => ({
        label: String(currentYear - i),
        value: currentYear - i,
    })),
];

export default function Dashboard() {
    const { theme } = useContext(ThemeContext);
    const { activeCity } = useAuth();
    const {
        totalUsers,
        totalInvoices,
        totalInvoicesValue,
        totalPendingInvoices,
        invoicesStats,
        invoicesByType,
        invoicesImportHistory,
        allUpcomingRaffles,
        allCombinedRaffles,
        latestPendingRaffles,
        topCompanies,
        topCompaniesAll,
        topCompaniesNFC,
        topCompaniesNFS,
        topCompaniesNFE,
        topUsers,
        typeTopUserSelected,
        setTypeTopUserSelected,
        sortByTopUser,
        setSortByTopUser,
        isLoadingTopUsers,
        socialInstitutionsStats,
        hasSocialInstitutionModule,
        finishedRaffles,
        registrationHistory,
        registrationPeriod,
        setRegistrationPeriod,
        isLoading,
        selectedYear,
        allYears,
        selectedRaffleId,
        selectedRaffle,
        handleSelectYear,
        handleAllYears,
        handleSelectRaffle,
        isAdmin,
        availableCities,
        selectedCityId,
        handleSelectCity,
        typeInvoiceSelected,
        setTypeInvoiceSelected,
        componentToPrintRef,
        handlePrint,
        buttonReportDisabled,
    } = useDashboard();

    const raffleOptions = useMemo(() => {
        const filtered = allCombinedRaffles.filter((r) => {
            if (allYears) return true;

            const checkYear = (dateStr?: string) => {
                if (!dateStr) return false;
                if (dateStr.includes(String(selectedYear))) return true;
                const parsed = new Date(dateStr);
                return !isNaN(parsed.getFullYear()) && (
                    parsed.getFullYear() === selectedYear ||
                    parsed.getUTCFullYear() === selectedYear
                );
            };

            return checkYear(r.dateOfRaffle) || checkYear(r.endPeriod) || checkYear(r.startPeriod);
        });

        return [
            { label: 'Todos os sorteios', value: 'all' as const },
            ...filtered.map((r) => {
                const dateFormatted = r.dateOfRaffle ? new Date(r.dateOfRaffle).toLocaleDateString('pt-BR') : '';
                return {
                    value: r.id,
                    label: dateFormatted ? `${r.description} (${dateFormatted})` : r.description,
                };
            }),
        ];
    }, [allCombinedRaffles, allYears, selectedYear]);

    const reportCity = (() => {
        if (isAdmin && selectedCityId) {
            const found = (availableCities as Array<{ id?: number; name: string; uf: string }>).find(c => c.id === selectedCityId);
            if (found) return { city: found.name, uf: found.uf } as ICity;
        }
        return activeCity;
    })();

    return (
        <div style={{ background: theme === 'light' ? '#f0f2f5' : '#111', padding: 10, margin: -24, minHeight: 'calc(100% + 48px)' }}>
            <Card
                bordered={false}
                style={{
                    marginTop: 4,
                    marginBottom: 16,
                    borderRadius: 8,
                    background: theme === 'dark' ? "#1f3b73" : "#1677ff",
                    boxShadow: "0 8px 24px rgba(22,119,255,.15)",
                }}
                bodyStyle={{ padding: "22px 26px" }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 16,
                        flexWrap: "wrap",
                    }}
                >
                    <div>
                        <Typography.Title
                            level={2}
                            style={{
                                margin: 0,
                                color: "#fff",
                            }}
                        >
                            Dashboard
                        </Typography.Title>

                        <Typography.Text
                            style={{
                                color: "rgba(255,255,255,.85)",
                                fontSize: 16,
                            }}
                        >
                            Acompanhe indicadores e estatísticas das notas fiscais.
                        </Typography.Text>

                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            alignItems: "flex-end",
                        }}
                    >
                        <div style={{ width: 120, flexShrink: 0 }}>
                            <div
                                style={{
                                    color: "rgba(255,255,255,.75)",
                                    fontSize: 12,
                                    marginBottom: 2,
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                }}
                            >
                                Ano
                            </div>

                            <Select
                                value={allYears ? "all" : selectedYear}
                                onChange={(val) =>
                                    val === "all"
                                        ? handleAllYears()
                                        : handleSelectYear(val as number)
                                }
                                options={yearOptions}
                                size="large"
                                style={{ width: "100%" }}
                            />
                        </div>

                        <div style={{ width: 200, minWidth: 160, flexShrink: 1 }}>
                            <div
                                style={{
                                    color: "rgba(255,255,255,.75)",
                                    fontSize: 12,
                                    marginBottom: 2,
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                }}
                            >
                                Sorteio
                            </div>

                            <Select
                                value={selectedRaffleId}
                                onChange={(val) => handleSelectRaffle(val)}
                                options={raffleOptions}
                                size="large"
                                showSearch
                                optionFilterProp="label"
                                popupMatchSelectWidth={false}
                                style={{ width: "100%" }}
                            />
                        </div>

                        {isAdmin && (
                            <div style={{ width: 200, minWidth: 160, flexShrink: 1 }}>
                                <div
                                    style={{
                                        color: "rgba(255,255,255,.75)",
                                        fontSize: 12,
                                        marginBottom: 2,
                                        fontWeight: 500,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Município
                                </div>

                                <Select
                                    value={selectedCityId ?? "all"}
                                    onChange={(val) =>
                                        handleSelectCity(
                                            val === "all" ? undefined : (val as number)
                                        )
                                    }
                                    options={[
                                        {
                                            label: "Todos os municípios",
                                            value: "all",
                                        },
                                        ...availableCities.map((c) => ({
                                            value: c.id!,
                                            label: `${c.name} - ${c.uf}`,
                                        })),
                                    ]}
                                    size="large"
                                    showSearch
                                    optionFilterProp="label"
                                    popupMatchSelectWidth={false}
                                    style={{ width: "100%" }}
                                />
                            </div>
                        )}

                        <div style={{ flexShrink: 0 }}>
                            <Button
                                icon={<FilePdfOutlined />}
                                onClick={handlePrint}
                                disabled={buttonReportDisabled}
                                loading={buttonReportDisabled}
                                size="large"
                                style={{
                                    background: '#fff',
                                    border: '1px solid #fff',
                                    color: '#000',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Gerar relatório
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>

            {/* <Title level={4}>Dashboard</Title>
            <div className="flex items-center gap-2 mt-4 mb-6">
                <Select
                    value={allYears ? 'all' : selectedYear}
                    onChange={(val) => val === 'all' ? handleAllYears() : handleSelectYear(val as number)}
                    options={yearOptions}
                    style={{ width: 160 }}
                />
                {isAdmin && (
                    <Select
                        value={selectedCityId ?? 'all'}
                        onChange={(val) => handleSelectCity(val === 'all' ? undefined : val as number)}
                        options={[
                            { label: 'Todos os municípios', value: 'all' as const },
                            ...availableCities.map((c) => ({ value: c.id!, label: `${c.name} - ${c.uf} ` })),
                        ]}
                        style={{ width: 220 }}
                    />
                )}
            </div> */}

            <Row gutter={[16, 16]} style={{ alignItems: 'stretch' }}>
                <Col xs={24} xl={12} xxl={6} style={{ display: 'flex', flexDirection: 'column' }}>
                    <StatCard
                        title="Total de Usuários"
                        mainValue={formatNumber(totalUsers)}
                        numericValue={totalUsers}
                        formatter={formatNumber}
                        subtitle="Cadastrados"
                        isLoading={isLoading}
                        color="#1c6dd0"
                        bgColor="#1c6dd022"
                        icon={<TeamOutlined />}
                    />
                </Col>
                <Col xs={24} xl={12} xxl={6} style={{ display: 'flex', flexDirection: 'column' }}>
                    <StatCard
                        title="Notas Válidas"
                        mainValue={formatNumber(invoicesStats.totalValid)}
                        numericValue={invoicesStats.totalValid}
                        formatter={formatNumber}
                        secondaryLabel="Total importado"
                        secondaryValue={formatNumber(totalInvoices)}
                        isLoading={isLoading}
                        color="#52c41a"
                        bgColor="#f6ffed"
                        icon={<FileTextOutlined />}
                    />
                </Col>
                <Col xs={24} xl={12} xxl={6} style={{ display: 'flex', flexDirection: 'column' }}>
                    <StatCard
                        title="Valor das Válidas"
                        mainValue={formatCurrency(invoicesStats.totalValue)}
                        numericValue={invoicesStats.totalValue}
                        formatter={formatCurrency}
                        secondaryLabel="Total importado"
                        secondaryValue={formatCurrency(totalInvoicesValue)}
                        isLoading={isLoading}
                        color="#08979c"
                        bgColor="#e6fffb"
                        icon={<DollarOutlined />}
                    />
                </Col>
                <Col xs={24} xl={12} xxl={6} style={{ display: 'flex', flexDirection: 'column' }}>
                    <StatCard
                        title="Notas em processamento"
                        mainValue={formatNumber(totalPendingInvoices)}
                        numericValue={totalPendingInvoices}
                        formatter={formatNumber}
                        subtitle="Aguardando validação"
                        isLoading={isLoading}
                        color="#fa8c16"
                        bgColor="#fff7e6"
                        icon={<ClockCircleOutlined />}
                    />
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16, alignItems: 'stretch' }}>
                <Col xs={24} xl={12} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="dashboard-chart-zoom">
                        <RegistrationHistoryChart
                            data={registrationHistory}
                            isLoading={isLoading}
                            period={registrationPeriod}
                            onPeriodChange={setRegistrationPeriod}
                            isRaffleFiltered={selectedRaffleId !== 'all'}
                        />
                    </div>
                </Col>
                <Col xs={24} xl={12} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="dashboard-chart-zoom">
                        <InvoicesImportHistoryChart data={invoicesImportHistory} isLoading={isLoading} />
                    </div>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16, alignItems: 'stretch' }}>
                <Col xs={24} xl={10} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="dashboard-chart-zoom" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <InvoicesByTypeChart data={invoicesByType} isLoading={isLoading} />
                    </div>
                </Col>
                <Col xs={24} xl={14} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="dashboard-chart-zoom" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <RaffleCalendar data={allUpcomingRaffles} isLoading={isLoading} />
                    </div>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16, alignItems: 'stretch' }}>
                <Col xs={24} xl={12} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="dashboard-chart-zoom" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <TopUsersList
                            data={topUsers}
                            isLoading={isLoadingTopUsers}
                            typeSelected={typeTopUserSelected}
                            onTypeChange={setTypeTopUserSelected}
                            sortBy={sortByTopUser}
                            onSortByChange={setSortByTopUser}
                        />
                    </div>
                </Col>
                <Col xs={24} xl={12} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="dashboard-chart-zoom" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <TopCompaniesList
                            data={topCompanies}
                            isLoading={isLoading}
                            hasCitySelected={selectedCityId ? true : false}
                            typeSelected={typeInvoiceSelected}
                            onTypeChange={setTypeInvoiceSelected}
                        />
                    </div>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16, alignItems: 'stretch' }}>
                {hasSocialInstitutionModule && (
                    <Col xs={24} xl={12} style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className="dashboard-chart-zoom" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <SocialInstitutionsStats
                                data={socialInstitutionsStats}
                                totalUsers={totalUsers}
                                isLoading={isLoading}
                                hasSocialInstitutionModule={hasSocialInstitutionModule}
                            />
                        </div>
                    </Col>
                )}
                <Col xs={24} xl={hasSocialInstitutionModule ? 12 : 24} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="dashboard-chart-zoom" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <FinishedRafflesList
                            data={finishedRaffles}
                            isLoading={isLoading}
                        />
                    </div>
                </Col>
            </Row>
            <ThemeContext.Provider value={{ theme: 'light', updateTheme: () => { } }}>
                <ConfigProvider theme={{ algorithm: antdTheme.defaultAlgorithm }}>
                    <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '750px' }}>
                        <ReportDashboard
                            refToPrint={componentToPrintRef}
                            activeCity={reportCity}
                            totalUsers={totalUsers}
                            totalInvoices={totalInvoices}
                            totalInvoicesValue={totalInvoicesValue}
                            totalPendingInvoices={totalPendingInvoices}
                            invoicesStats={invoicesStats}
                            topCompanies={topCompanies}
                            topCompaniesAll={topCompaniesAll}
                            topCompaniesNFC={topCompaniesNFC}
                            topCompaniesNFS={topCompaniesNFS}
                            topCompaniesNFE={topCompaniesNFE}
                            topUsers={topUsers}
                            socialInstitutionsStats={socialInstitutionsStats}
                            hasSocialInstitutionModule={hasSocialInstitutionModule}
                            finishedRaffles={finishedRaffles}
                            allUpcomingRaffles={allUpcomingRaffles}
                            registrationHistory={registrationHistory}
                            registrationPeriod={registrationPeriod}
                            invoicesImportHistory={invoicesImportHistory}
                            invoicesByType={invoicesByType}
                            selectedYear={selectedYear}
                            allYears={allYears}
                            selectedRaffle={selectedRaffle}
                            latestPendingRaffles={latestPendingRaffles}
                        />
                    </div>
                </ConfigProvider>
            </ThemeContext.Provider>
        </div>
    );
}
